// FIG Behavior (DESIGN_SPEC §11.4.5, §10.12, §8 score arithmetic; RESTYLE §2). Props: FigProps { ctx, no }.
// Renders its own journal cell: s8 beside FIG ATT&CK, the full row when ATT&CK has nothing to show. Severity groups open
// with a tinted chip (the threat-tag tint of their severity); medium and above rows keep the 2px edge.
//   meta(ctx) → { id: "behavior", label: "Behavior", count: tags, empty: live or no verdict }
import { useMemo, useState } from "react";
import moth72 from "@/assets/specimen/moth-72.json";
import { Ascii, Empty, FigHead, Plate, Sev, Specimen, ThreatTag, figTitleId } from "@/components/ui";
import { count, envPath, rel } from "@/lib/format";
import { classifyNoise, serviceOfName } from "@/lib/derive/noise";
import type { FigMeta, FigProps, NoiseIndex, ReportCtx } from "@/lib/derive/types";
import { reportVerdict } from "@/lib/derive/verdict";
import type { Event, Severity, Tag } from "@/types";
import { meta as attackMeta } from "./AttackFig";
import { techniqueHref } from "../derive/attack";
import { nodeAt, pathKey, treeOf, type Tree } from "../derive/tree";

export function meta(ctx: ReportCtx): FigMeta {
  const v = ctx.mode === "live" ? null : reportVerdict(ctx.stream, ctx.behaviorReport);
  return { id: "behavior", label: "Behavior", count: v?.tags.length ?? 0, empty: !v };
}

const SEV_WORD = ["info", "low", "medium", "high", "critical"] as const;
const GROUPS: Severity[] = [5, 4, 3, 2, 1];
/** Untrusted payloads: a severity outside 1–5 is clamped, never dropped. */
const sevOf = (t: Tag): Severity => Math.max(1, Math.min(5, Math.round(Number(t.severity) || 1))) as Severity;

// ---- evidence, joined to the events it names ---------------------------------------------

type EvLine = { text: string; note?: string };
type EvIndex = {
  paths: Map<string, { shown: string; writeT?: number; startT?: number; startPid?: number; loadT?: number; loadPid?: number }>;
  injects: Map<string, { t: number; seq: number }>;
  regs: Map<string, { t: number; pid?: number }>;
  procs: { cmd: string; pid: number; t: number }[];
  hosts: Map<string, number>;
};

const PATH_RE = /^([a-z]:\\|%[a-z_]+%\\|\\\\[^\\])/i;
const REG_RE = /^(hkey_|hk(cu|lm|cr|u|cc)\\|\\registry\\)/i;
const HOSTISH_RE = /^[a-z0-9._-]+\.[a-z0-9-]+(\/\S*)?$/i;
const HIVES: [RegExp, string][] = [
  [/^hkey_current_user(?=\\|$)/, "hkcu"],
  [/^hkey_local_machine(?=\\|$)/, "hklm"],
  [/^hkey_classes_root(?=\\|$)/, "hkcr"],
  [/^hkey_users(?=\\|$)/, "hku"],
  [/^hkey_current_config(?=\\|$)/, "hkcc"],
  [/^\\registry\\machine(?=\\|$)/, "hklm"],
];
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const normText = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
function regKey(k: string): string {
  const low = k.trim().toLowerCase().replace(/\\+$/, "");
  for (const [re, short] of HIVES) if (re.test(low)) return low.replace(re, short);
  return low;
}

function indexEvents(events: readonly Event[], t0: number): EvIndex {
  const idx: EvIndex = { paths: new Map(), injects: new Map(), regs: new Map(), procs: [], hosts: new Map() };
  const path = (p: string) => {
    const k = pathKey(p);
    let info = idx.paths.get(k);
    if (!info) idx.paths.set(k, (info = { shown: envPath(p.replace(/\\+$/, "")) }));
    return info;
  };
  const first = (m: Map<string, number>, k: string, t: number) => {
    if (k && !m.has(k)) m.set(k, t);
  };
  for (const e of events) {
    const f = e.fields ?? {};
    const ms = Date.parse(e.ts);
    const t = Number.isFinite(ms) ? ms - t0 : 0;
    switch (e.kind) {
      case "file.create":
      case "file.write":
      case "file.drop":
      case "file.rename": {
        const p = str(e.kind === "file.rename" ? f.to : f.path);
        if (p) path(p).writeT ??= t;
        break;
      }
      case "proc.start": {
        const img = str(f.image);
        const pid = Number(f.pid);
        if (img) {
          const info = path(img);
          if (info.startT === undefined) Object.assign(info, { startT: t, startPid: pid });
        }
        if (str(f.cmdline)) idx.procs.push({ cmd: normText(str(f.cmdline)), pid, t });
        break;
      }
      case "image.load": {
        const img = str(f.image);
        if (img) {
          const info = path(img);
          if (info.loadT === undefined) Object.assign(info, { loadT: t, loadPid: Number(f.pid) });
        }
        break;
      }
      case "thread.inject": {
        const k = `${str(f.creator_pid)}>${str(f.target_pid)}`;
        if (!idx.injects.has(k)) idx.injects.set(k, { t, seq: e.seq });
        break;
      }
      case "registry.set":
      case "registry.delete": {
        const key = regKey(str(f.key));
        if (key && !idx.regs.has(key)) idx.regs.set(key, { t, pid: typeof f.pid === "number" ? f.pid : undefined });
        break;
      }
      case "net.dns":
        first(idx.hosts, str(f.qname ?? f.name).toLowerCase().replace(/\.$/, ""), t);
        break;
      case "net.http": {
        const host = str(f.host).toLowerCase().replace(/:\d+$/, "");
        first(idx.hosts, host, t);
        first(idx.hosts, `${host}${str(f.path).toLowerCase()}`, t);
        break;
      }
      default:
        break;
    }
  }
  return idx;
}

function evidenceLine(raw: string, idx: EvIndex, tree: Tree, noise: Pick<NoiseIndex, "bgNames">): EvLine {
  const text = raw.trim();
  const inj = /creator=(\d+)\s+target=(\d+)/i.exec(text);
  if (inj) {
    const hit = idx.injects.get(`${inj[1]}>${inj[2]}`);
    if (!hit) return { text };
    const target = nodeAt(tree, Number(inj[2]), hit.seq);
    return { text, note: [target?.name, `at ${rel(hit.t)}`].filter(Boolean).join(" ") };
  }
  const yara = /^(\S.*?)\s+@\s+(\S.*)$/.exec(text);
  if (yara) {
    const art = yara[2].toLowerCase();
    const dump = tree.dumps.find((d) => d.name?.toLowerCase() === art);
    if (dump) {
      const n = nodeAt(tree, dump.pid, dump.seq);
      return { text, note: `dump of ${n?.name ? `${n.name} ` : "pid "}${dump.pid}` };
    }
    const root = tree.roots.length ? tree.nodes.get(tree.roots[0]) : undefined;
    return root?.name.toLowerCase() === art ? { text, note: "the sample" } : { text };
  }
  if (PATH_RE.test(text)) {
    const info = idx.paths.get(pathKey(text));
    if (!info) return { text: envPath(text) };
    const notes = [
      info.writeT !== undefined && `written ${rel(info.writeT)}`,
      info.startT !== undefined && `started ${rel(info.startT)} as pid ${info.startPid}`,
      info.startT === undefined && info.loadT !== undefined && `loaded ${rel(info.loadT)} by pid ${info.loadPid}`,
    ].filter(Boolean) as string[];
    return { text: info.shown, note: notes.join(" · ") || undefined };
  }
  if (REG_RE.test(text)) {
    const hit = idx.regs.get(regKey(text));
    return hit ? { text, note: `set ${rel(hit.t)}${hit.pid ? ` by pid ${hit.pid}` : ""}` } : { text };
  }
  let host = "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) {
    try {
      const u = new URL(text);
      host = `${u.hostname}${u.pathname === "/" ? "" : u.pathname}`.toLowerCase();
    } catch {
      host = "";
    }
  } else if (HOSTISH_RE.test(text)) host = text.toLowerCase().replace(/\.$/, "");
  if (host) {
    const t = idx.hosts.get(host);
    const name = host.split("/")[0];
    const svc = noise.bgNames.get(name) ?? serviceOfName(name);
    const notes = [t !== undefined && `first ${rel(t)}`, svc && `Windows background, ${svc}`].filter(Boolean) as string[];
    return { text, note: notes.join(" · ") || undefined };
  }
  // The rules cut long command lines with a trailing "..."; match on what is left.
  const needle = normText(text.replace(/(\.\.\.|…)$/, ""));
  const proc =
    needle.length < 8
      ? undefined
      : (idx.procs.find((p) => p.cmd === needle) ?? idx.procs.find((p) => p.cmd.startsWith(needle)) ?? idx.procs.find((p) => p.cmd.includes(needle)));
  return proc ? { text, note: `pid ${proc.pid} at ${rel(proc.t)}` } : { text };
}

// ---- view ----------------------------------------------------------------------------

const EV_SHOWN = 3;

function TagRow({ tag, lines }: { tag: Tag; lines: EvLine[] }) {
  const [open, setOpen] = useState(false);
  const shown = open ? lines : lines.slice(0, EV_SHOWN);
  const mitre = (tag.mitre_ids ?? []).filter((m) => m && m.trim());
  return (
    <article className="tagrow" data-sev={sevOf(tag)}>
      <div className="min-w-0">
        <h3>
          <span className="tag-title">{tag.title}</span>
          <span className="tag-id" translate="no">
            {tag.id}
          </span>
        </h3>
        {tag.description && <p className="tag-desc">{tag.description}</p>}
        {lines.length > 0 && (
          <div className="tag-ev">
            {shown.map((l, i) => (
              <code key={i} translate="no" spellCheck={false}>
                {l.text}
                {l.note && <span className="text-ink-mute"> · {l.note}</span>}
              </code>
            ))}
            {lines.length > EV_SHOWN && (
              <button
                type="button"
                className="mt-0.5 justify-self-start border-0 border-b border-dotted border-ink-mute bg-transparent p-0 font-mono text-meta text-ink-soft hover:border-solid hover:text-ink"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
              >
                {open ? "show fewer" : `+ ${lines.length - EV_SHOWN} more`}
              </button>
            )}
          </div>
        )}
      </div>
      <div className="tag-side">
        {mitre.length ? (
          mitre.map((m) => (
            <a key={m} className="tech-id" href={techniqueHref(m)} target="_blank" rel="noopener noreferrer" translate="no">
              {m.trim().toUpperCase()}
            </a>
          ))
        ) : (
          <span className="text-ink-faint">no technique</span>
        )}
        <span>×{count(tag.hits)}</span>
      </div>
    </article>
  );
}

export default function BehaviorFig({ ctx, no }: FigProps) {
  const v = ctx.mode === "live" ? null : reportVerdict(ctx.stream, ctx.behaviorReport);
  const tags = v?.tags;
  const events = ctx.stream.snap.events;
  const version = ctx.stream.snap.version;
  const t0 = ctx.stream.t0 ?? (events.length ? Date.parse(events[0].ts) : 0);
  const tree = treeOf(ctx);
  // The events array is one append-only instance: snap.version is what changes.
  const idx = useMemo(() => indexEvents(events, t0), [events, version, t0]);
  const shared = ctx.noise;
  const noise = useMemo(() => shared ?? classifyNoise(events), [shared, events, version]);
  const lines = useMemo(() => {
    const m = new Map<Tag, EvLine[]>();
    for (const t of tags ?? []) {
      const ev = [...new Set((t.evidence ?? []).map((e) => (typeof e === "string" ? e.trim() : "")).filter((e) => e !== ""))];
      m.set(t, ev.map((e) => evidenceLine(e, idx, tree, noise)));
    }
    return m;
  }, [tags, idx, tree, noise]);
  const wide = attackMeta(ctx).empty;
  const cell = wide ? "cell fig" : "cell s8 fig";

  if (!v || !tags) {
    return (
      <section className={cell} aria-labelledby={figTitleId(no)}>
        <FigHead no={no} label="Behavior" />
        <Empty inline>Not scored yet</Empty>
      </section>
    );
  }

  // A verdict that counts tags it did not deliver (a record without tags[]): say so, never "no rule fired".
  const counted = ctx.stream.verdict?.tagCount ?? 0;
  if (tags.length === 0 && counted > 0) {
    return (
      <section className={cell} aria-labelledby={figTitleId(no)}>
        <FigHead no={no} label="Behavior" count={`${count(counted)} ${counted === 1 ? "tag" : "tags"}`} />
        <Empty inline>Tag details not delivered</Empty>
      </section>
    );
  }

  const ordered = GROUPS.map((g) => [g, tags.filter((t) => sevOf(t) === g)] as const).filter(([, list]) => list.length > 0);
  const hits = tags.reduce((s, t) => s + (Number(t.hits) || 0), 0);

  return (
    <section className={cell} aria-labelledby={figTitleId(no)}>
      <FigHead
        no={no}
        label="Behavior"
        count={tags.length ? `${count(tags.length)} ${tags.length === 1 ? "tag" : "tags"} · ${count(hits)} ${hits === 1 ? "hit" : "hits"}` : "0 tags"}
      />
      {tags.length === 0 ? (
        <div className="fig-body grid grid-cols-[minmax(0,15rem)_minmax(0,1fr)] items-center gap-x-cell gap-y-4 max-[720px]:grid-cols-1">
          <Specimen label="An empty specimen plate: no behavior rule fired" className="max-w-60">
            <Plate sm>
              <Ascii art={moth72} maxPx={4.6} />
            </Plate>
          </Specimen>
          <Empty inline>
            {events.length ? `No rule fired · ${count(events.length)} events` : "No rule fired"}
          </Empty>
        </div>
      ) : (
        <div className="fig-body">
          {ordered.map(([g, list]) => (
            <div key={g} className="sevgroup">
              <div className="sevgroup-h">
                <ThreatTag sev={g} className="h-[22px] uppercase tracking-[.12em]">
                  <Sev level={g} label={false} className="mr-2 [--sc:var(--tt-fg)]" />
                  {SEV_WORD[g - 1]}
                </ThreatTag>
                <span className="ct">
                  {list.length} {list.length === 1 ? "tag" : "tags"}
                </span>
              </div>
              {list.map((t, i) => (
                <TagRow key={`${t.id}:${i}`} tag={t} lines={lines.get(t) ?? []} />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
